import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Search, Filter, Download,
  Edit, Trash2, Eye, Phone, Mail, MapPin,
  FileText, CreditCard, Wifi, Shield
} from 'lucide-react';
import api from '../../services/api';
import DataTable from '../../components/Table/DataTable';
import PermissionButton from '../../components/Auth/PermissionButton';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ExportButtons from '../../components/Export/ExportButtons';

const Clientes = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete, canView } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'todos',
    tipo: 'todos',
    fidelizacao: 'todos'
  });

  const fetchClientes = async () => {
    try {
      setLoading(true);
      
      let url = `/clientes?page=${pagination.page}&limit=${pagination.limit}`;
      
      if (filters.search) {
        url = `/clientes/search?nome=${filters.search}&page=${pagination.page}&limit=${pagination.limit}`;
      }
      
      const response = await api.get(url);
      setClientes(response.data.data);
      setPagination({
        ...pagination,
        total: response.data.total,
        totalPages: response.data.totalPages
      });
    } catch (error) {
      toast.error('Erro ao carregar clientes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, [pagination.page, pagination.limit]);

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchClientes();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este cliente?')) {
      try {
        await api.delete(`/clientes/${id}`);
        toast.success('Cliente excluído com sucesso');
        fetchClientes();
      } catch (error) {
        toast.error('Erro ao excluir cliente');
      }
    }
  };

  const handleToggleStatus = async (cliente) => {
    try {
      const endpoint = cliente.ativo ? 'desativar' : 'ativar';
      await api.patch(`/clientes/${cliente.id}/${endpoint}`);
      toast.success(`Cliente ${cliente.ativo ? 'desativado' : 'ativado'} com sucesso`);
      fetchClientes();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const columns = [
    {
      header: 'Ações',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/clientes/${row.original.id}/detalhes`)}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Detalhes"
          >
            <Eye className="w-4 h-4" />
          </button>
          
          {canEdit('clientes') && (
            <button
              onClick={() => navigate(`/clientes/${row.original.id}/editar`)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          
          {canDelete('clientes') && (
            <button
              onClick={() => handleDelete(row.original.id)}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      )
    },
    {
      header: 'Nome',
      accessorKey: 'nome',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {row.original.nome}
            </p>
            <p className="text-xs text-gray-500">
              {row.original.tipoPessoa === 'F' ? 'CPF' : 'CNPJ'}: {row.original.cpfCnpj}
            </p>
          </div>
          {row.original.temFidelizacao && (
            <Shield className="w-4 h-4 text-blue-600" title="Cliente com contrato fidelizado" />
          )}
        </div>
      )
    },
    {
      header: 'Contato',
      accessorKey: 'email',
      cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-sm">
            <Mail className="w-3 h-3 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              {row.original.email || '-'}
            </span>
          </div>
          {row.original.telefones?.[0] && (
            <div className="flex items-center gap-1 text-sm">
              <Phone className="w-3 h-3 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400">
                {row.original.telefones[0].numero}
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Vencimento',
      accessorKey: 'diaVencimento',
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Dia {getValue() || '-'}
        </span>
      )
    },
    {
      header: 'Status',
      accessorKey: 'ativo',
      cell: ({ row }) => (
        <button
          onClick={() => handleToggleStatus(row.original)}
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.original.ativo
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
          }`}
        >
          {row.original.ativo ? 'Ativo' : 'Inativo'}
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Clientes
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gerencie os clientes do sistema
          </p>
        </div>
        
        {canCreate('clientes') && (
          <button
            onClick={() => navigate('/clientes/novo')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Novo Cliente
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome, CPF/CNPJ..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
              />
            </div>
          </div>
          
          <select
            value={filters.tipo}
            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
          >
            <option value="todos">Todos os Tipos</option>
            <option value="F">Pessoa Física</option>
            <option value="J">Pessoa Jurídica</option>
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
          >
            <option value="todos">Todos</option>
            <option value="ativos">Ativos</option>
            <option value="inativos">Inativos</option>
          </select>
          
          <select
            value={filters.fidelizacao}
            onChange={(e) => setFilters({ ...filters, fidelizacao: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
            title="Filtrar por fidelização"
          >
            <option value="todos">Fidelização: Todos</option>
            <option value="com_fidelizacao">Com Fidelização</option>
            <option value="sem_fidelizacao">Sem Fidelização</option>
          </select>
          
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Buscar
          </button>
          
          <ExportButtons 
            data={clientes}
            filename="clientes"
            title="Relatório de Clientes"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
        <DataTable
          columns={columns}
          data={clientes}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => setPagination({ ...pagination, page })}
          onLimitChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
        />
      </div>
    </div>
  );
};

export default Clientes;